import React, { useState, useEffect } from "react";
import { Tag } from "antd";
import AddProductBundle from "./AddProductBundle";
import { getProductBundles, getProductBundleById } from "../../apis/productBundles";
import { ProductBundle } from "../../interfaces/ProductBundle";
import CommonPagination from "../../components/CommanPagination";

const ProductBundlesList: React.FC = () => {
  const [bundles, setBundles] = useState<ProductBundle[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editableBundle, setEditableBundle] = useState<ProductBundle | null>(null);
  const [meta, setMeta] = useState<any>();

  const fetchBundles = (params: any = { page: 1, per_page: 10 }) => {
    setLoading(true);
    getProductBundles(params)
      .then((res: any) => {
        const data = Array.isArray(res) ? res : res?.data ?? [];
        setBundles(Array.isArray(data) ? data : []);
         setMeta(res.meta);
      })
      .catch((err: any) => console.error("Failed to fetch bundles:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchBundles();
  }, []);

   const onPaginationChange = (page: number, pageSize?: number) => {
  fetchBundles({ page, per_page: pageSize }); 
};

  const openModal = () => {
    setEditableBundle(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditableBundle(null);
  };

  const handleSuccess = () => {
    closeModal();
    fetchBundles();
  };

  const handleEdit = (bundle: ProductBundle) => {
    const id = bundle.id;
    getProductBundleById(id)
      .then((res: any) => {
        const b = res?.data ?? res ?? bundle;
        setEditableBundle(b);
        setIsModalOpen(true);
      })
      .catch((err: unknown) => console.error("Failed to fetch bundle:", err));
  };

  const isActive = (b: ProductBundle) =>
    b.isActive ?? b.is_active ?? true;

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            Product Bundles
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Create and manage pharmacy item bundles.
          </p>
        </div>
        <div className="flex gap-3 mt-4 sm:mt-0">
          <button
            type="button"
            onClick={openModal}
            className="block px-3 py-2 text-sm font-semibold text-center text-white rounded-md shadow-sm bg-primary hover:bg-hover"
          >
            Add Bundle
          </button>
        </div>
      </div>

      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              {loading ? (
                <div className="min-h-[200px] flex items-center justify-center bg-gray-50/50">
                  <p className="text-sm text-gray-500">Loading...</p>
                </div>
              ) : bundles.length === 0 ? (
                <div className="min-h-[200px] flex items-center justify-center bg-gray-50/50 rounded-lg border-2 border-dashed border-gray-200">
                  <div className="text-center">
                    <p className="text-sm text-gray-600">
                      No bundles yet. Click &quot;Save Bundle&quot; to create a new
                      pharmacy item bundle.
                    </p>
                    <button
                      type="button"
                      onClick={openModal}
                      className="mt-3 text-sm font-medium text-primary hover:underline"
                    >
                      Create your first bundle
                    </button>
                  </div>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                        Name
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Code
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Description
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Status
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {bundles.map((bundle) => (
                      <tr key={bundle.id}>
                        <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                          {bundle.name}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-700">
                          {bundle.code}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-600 max-w-xs truncate">
                          {bundle.description || "—"}
                        </td>
                        <td className="px-3 py-4 text-sm whitespace-nowrap">
                          <Tag color={isActive(bundle) ? "green" : "red"}>
                            {isActive(bundle) ? "Active" : "Inactive"}
                          </Tag>
                        </td>
                        <td className="py-4 px-3 text-sm text-gray-700">
                        <button
                           onClick={() => handleEdit(bundle)}
                          className="block px-3 py-2 text-white bg-primary rounded hover:bg-hover"
                        >
                          Edit
                        </button>
                      </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
         <CommonPagination meta={meta} onChange={onPaginationChange} />
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[96vh] overflow-y-auto relative">
            <div className="flex items-center justify-between bg-primary text-white px-4 py-3 rounded-t-lg">
              <h2 className="text-lg font-semibold">
                {editableBundle ? "Edit Bundle" : "Save Bundle"}
              </h2>
              <button
                onClick={closeModal}
                className="text-white hover:text-gray-200 text-xl font-bold"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <AddProductBundle
                closeModal={closeModal}
                onSuccess={handleSuccess}
                editableBundle={editableBundle ?? undefined}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductBundlesList;
